import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'

// Module-level cache of the global settings doc, shared across hook instances so
// open panels/drawer only hit /api/profitability-settings once (settings are
// global + rarely change). Mirrors useRouteInterStopDrive's _routesCache.
let _settingsCache = null
// Monotonic request token: every fetch (mount OR refresh, across all instances)
// claims the next number; only the highest-numbered fetch to resolve is allowed
// to write the cache. Prevents a slow initial GET from clobbering a newer
// refresh() result (last-issued wins, not last-to-resolve).
let _reqSeq = 0
export const __resetSettingsCache = () => { _settingsCache = null; _reqSeq = 0 }

// Maps the raw singleton doc → the 4 keys the calc consumes. The calc wants the
// target margin under the name `target` (targetMargin is the same RATIO). Drops
// _id/__v/updatedAt/updatedBy. Exported for direct unit testing.
export const mapDoc = (doc) => doc ? {
  target: doc.targetMargin,
  burdenRate: doc.burdenRate,
  mileageRate: doc.mileageRate,
  driveRoundTripFactor: doc.driveRoundTripFactor
} : null

// Fetches the global profitability settings once (module-cached) and exposes a
// `refresh()` to re-fetch after a save. `settings` is null while loading OR on
// error so the calc falls back to its own constants.
//
// `url` is the base API URL the consumer passes (props.url). Only a nullish url
// (undefined/null) is treated as a no-op (no fetch, settings null); an empty
// string '' is a valid same-origin base and DOES fetch (`${''}/api/...`).
export const useProfitabilitySettings = (url) => {
  const [settings, setSettings] = useState(() => mapDoc(_settingsCache))
  const [loading, setLoading] = useState(() => url != null && _settingsCache == null)
  // Guards every async setState so we never update an unmounted component (the
  // effect, the refresh() callback, and any concurrent fetch all share this).
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  // One fetch path for both mount and refresh — so the cache-poison guard, the
  // last-issued-wins token, and the mounted guard can't drift between callers.
  const fetchSettings = useCallback((base) => {
    const seq = ++_reqSeq
    return axios.get(`${base}/api/profitability-settings`)
      .then(res => {
        const doc = res?.data || null
        // Only the latest-issued fetch writes the cache (don't let a slow stale
        // GET clobber a newer one); only cache a real result (no poison on error).
        if (doc && seq === _reqSeq) _settingsCache = doc
        if (mountedRef.current && seq === _reqSeq) { setSettings(mapDoc(doc)); setLoading(false) }
        return mapDoc(doc)
      })
      .catch(() => {
        if (mountedRef.current && seq === _reqSeq) { setSettings(null); setLoading(false) }
        return null
      })
  }, [])

  useEffect(() => {
    if (url == null) { setSettings(null); setLoading(false); return }
    if (_settingsCache) { setSettings(mapDoc(_settingsCache)); setLoading(false); return }
    setLoading(true)
    fetchSettings(url)
  }, [url, fetchSettings])

  // Force a re-fetch even if cached (the modal calls this after a successful
  // PATCH so open panels recompute). On success refreshes the module cache too.
  const refresh = useCallback(() => {
    if (url == null) return Promise.resolve(null)
    setLoading(true)
    return fetchSettings(url)
  }, [url, fetchSettings])

  return { settings, loading, refresh }
}
